<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import FunnelAPI from '../../../api/funnel';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});
defineEmits([]);
const { t } = useI18n();
import agents from '../../../api/agents';
import KanbanItemForm from '../kanban/components/KanbanItemForm.vue';
import Modal from '../../../components/Modal.vue';
import KanbanItemDetails from '../kanban/components/KanbanItemDetails.vue';
import { emitter } from 'shared/helpers/mitt';
import Button from '../../../components-next/button/Button.vue';
import Avatar from '../../../components-next/avatar/Avatar.vue';
import KanbanAPI from '../../../api/kanban';
import offersAPI from '../../../api/offers';
import {
  formatKanbanCurrency,
  maskKanbanCurrencyTyping,
  parseKanbanCurrencyInput,
} from 'dashboard/helper/kanbanCurrencyMask';

const store = useStore();
const funnels = ref([]);

// Obtém os dados do contato e conversa atual
const currentChat = computed(() => store.getters.getSelectedChat);
const contactName = computed(
  () => currentChat.value?.meta?.sender?.name || 'Sem nome'
);

// Quick edit Valor/Oferta na sidebar - oferta SOMA no valor existente.
const quickValueEditing = ref(false);
const quickValueDraft = ref('');
const quickValueSaving = ref(false);
const showOffersDropdown = ref(false);
const offerBtn = ref(null);
const offersDropdownStyle = ref({});
const offersList = ref([]);
const offersSearchTerm = ref('');
const filteredOffersList = computed(() => {
  const term = offersSearchTerm.value.trim().toLowerCase();
  if (!term) return offersList.value;
  return offersList.value.filter(o => {
    const name = (o.title || o.name || o.description || '').toLowerCase();
    return name.includes(term);
  });
});
const offersLoading = ref(false);

const fetchOffersForDropdown = async () => {
  if (offersList.value.length || offersLoading.value) return;
  offersLoading.value = true;
  try {
    const res = await offersAPI.getOffers();
    offersList.value = Array.isArray(res.data) ? res.data : [];
  } catch (e) {
    return;
  } finally {
    offersLoading.value = false;
  }
};

// Posiciona o dropdown em coordenadas da viewport (position:fixed) a partir do
// retangulo do botao. Necessario porque o dropdown e teleportado pro <body>
// para nao ser cortado pelo overflow do painel estreito da conversa.
const positionOffersDropdown = () => {
  const rect = offerBtn.value?.getBoundingClientRect?.();
  if (!rect) return;
  const WIDTH = 288; // w-72
  const GAP = 6;
  const left = Math.round(
    Math.min(Math.max(8, rect.right - WIDTH), window.innerWidth - WIDTH - 8)
  );
  const style = { position: 'fixed', width: `${WIDTH}px`, left: `${left}px` };
  // Abre acima do botao; se nao houver espaco, abre abaixo.
  if (rect.top > 320) {
    style.bottom = `${Math.round(window.innerHeight - rect.top + GAP)}px`;
  } else {
    style.top = `${Math.round(rect.bottom + GAP)}px`;
  }
  offersDropdownStyle.value = style;
};

const closeOffersDropdown = () => {
  showOffersDropdown.value = false;
  offersSearchTerm.value = '';
  window.removeEventListener('scroll', closeOffersDropdown, true);
  window.removeEventListener('resize', closeOffersDropdown);
};

const toggleOffersDropdown = async () => {
  if (showOffersDropdown.value) {
    closeOffersDropdown();
    return;
  }
  showOffersDropdown.value = true;
  positionOffersDropdown();
  // Em scroll/resize o botao se move, mas o dropdown fixed nao acompanha:
  // fecha pra evitar desalinhamento (padrao comum de dropdown flutuante).
  window.addEventListener('scroll', closeOffersDropdown, true);
  window.addEventListener('resize', closeOffersDropdown);
  await fetchOffersForDropdown();
  await nextTick();
  positionOffersDropdown();
};

// Multi-card: lead pode ter N cards. Setas navegam, principal via lead_primary.
const currentCardIdx = ref(0);
const itemsSameConversation = ref([]);
const loadingSameConversation = ref(false);

const sortedKanbanItems = computed(() => {
  // Combina o item atual da chat (latest_kanban_item) com os outros itens
  // da mesma conversa carregados via fetchItemsSameConversation. Necessario
  // pq backend so retorna 1 kanban_item por conversa no GET /conversations.
  const chatItems = currentChat.value?.kanban_items || [];
  const others = itemsSameConversation.value || [];
  const byId = new Map();
  // others (stale fetch) primeiro, chatItems (atualizado pelo store) depois pra prevalecer
  [...others, ...chatItems].forEach(item => {
    if (item?.id) byId.set(item.id, item);
  });
  const items = Array.from(byId.values());
  return items.sort((a, b) => {
    const aP = a.item_details?.lead_primary ? 1 : 0;
    const bP = b.item_details?.lead_primary ? 1 : 0;
    if (aP !== bP) return bP - aP;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
});

// Obtém o item do kanban visível (multi-card: navegavel via setas).
const kanbanItemComputed = computed(() => {
  const items = sortedKanbanItems.value;
  if (!items.length) return null;
  // Clamp idx pra evitar out-of-bounds, mas nao mutamos currentCardIdx aqui (anti-pattern)
  const safeIdx = Math.max(0, Math.min(currentCardIdx.value, items.length - 1));
  return items[safeIdx];
});

const refreshChatKanbanItems = updatedItem => {
  const chat = currentChat.value;
  if (!chat?.id) return;
  const existing = Array.isArray(chat.kanban_items) ? chat.kanban_items : [];
  const merged = existing.map(i =>
    i.id === updatedItem.id ? { ...i, ...updatedItem } : i
  );
  if (!merged.find(i => i.id === updatedItem.id)) merged.push(updatedItem);
  store.dispatch('updateConversationKanbanItems', {
    conversationId: chat.id,
    kanbanItems: merged,
  });
};

const quickValueDisplay = computed(() => {
  const v = Number(kanbanItemComputed.value?.item_details?.value || 0);
  return formatKanbanCurrency(v) || 'R$ 0,00';
});

const startQuickValueEdit = () => {
  if (!kanbanItemComputed.value) return;
  const current = Number(kanbanItemComputed.value.item_details?.value || 0);
  quickValueDraft.value = current ? formatKanbanCurrency(current) : '';
  quickValueEditing.value = true;
};

const cancelQuickValueEdit = () => {
  quickValueEditing.value = false;
  quickValueDraft.value = '';
};

const onQuickValueInput = event => {
  quickValueDraft.value = maskKanbanCurrencyTyping(event?.target?.value);
  if (event?.target) event.target.value = quickValueDraft.value;
};

const saveQuickValue = async () => {
  if (!kanbanItemComputed.value || quickValueSaving.value) return;
  const num = parseKanbanCurrencyInput(quickValueDraft.value);
  if (num === null || num < 0) {
    quickValueEditing.value = false;
    return;
  }
  quickValueSaving.value = true;
  try {
    const current = kanbanItemComputed.value.item_details || {};
    const offers = Array.isArray(current.offers) ? [...current.offers] : [];
    const isManual = o =>
      o.description === 'Inserção Manual' ||
      o.description === 'Insercao Manual' ||
      o.name === 'Inserção Manual';
    const nonManualTotal = offers
      .filter(o => !isManual(o))
      .reduce((s, o) => s + Number(o.value || 0), 0);
    const manualDelta = num - nonManualTotal;
    const filtered = offers.filter(o => !isManual(o));
    if (manualDelta > 0) {
      filtered.push({
        description: 'Inserção Manual',
        name: 'Inserção Manual',
        value: manualDelta,
        currency: 'BRL',
        manual: true,
        added_at: new Date().toISOString(),
      });
    }
    const updated = {
      ...kanbanItemComputed.value,
      item_details: { ...current, offers: filtered, value: num },
      skip_appointment_emit: true,
    };
    await KanbanAPI.update(kanbanItemComputed.value.id, updated);
    emitter.emit('kanban.item.updated', updated);
    refreshChatKanbanItems(updated);
    quickValueEditing.value = false;
  } catch (e) {
    return;
  } finally {
    quickValueSaving.value = false;
  }
};

const totalCards = computed(() => sortedKanbanItems.value.length);

// Lista de "outros itens" para a secao abaixo - exclui o atual
const otherItemsSameConversation = computed(() => {
  const currentId = kanbanItemComputed.value?.id;
  return (itemsSameConversation.value || []).filter(i => i.id !== currentId);
});
const hasMultipleCards = computed(
  () => totalCards.value > 1 && otherItemsSameConversation.value.length >= 0
);

const currentCardFunnelName = computed(() => {
  const item = sortedKanbanItems.value[currentCardIdx.value];
  if (!item) return '';
  const funnel = funnels.value.find(f => f.id === item.funnel_id);
  return funnel?.name || `Funil #${item.funnel_id}`;
});

const isCurrentCardPrimary = computed(() => {
  const item = sortedKanbanItems.value[currentCardIdx.value];
  return !!item?.item_details?.lead_primary || currentCardIdx.value === 0;
});

const prevCard = () => {
  if (totalCards.value <= 1) return;
  currentCardIdx.value =
    (currentCardIdx.value - 1 + totalCards.value) % totalCards.value;
};

const nextCard = () => {
  if (totalCards.value <= 1) return;
  currentCardIdx.value = (currentCardIdx.value + 1) % totalCards.value;
};

const makePrimary = async () => {
  const visible = sortedKanbanItems.value[currentCardIdx.value];
  if (!visible) return;
  try {
    const updatedVisible = {
      ...visible,
      item_details: { ...(visible.item_details || {}), lead_primary: true },
    };
    await KanbanAPI.update(visible.id, updatedVisible);
    const others = sortedKanbanItems.value.filter(i => i.id !== visible.id);
    await others
      .filter(other => other.item_details?.lead_primary)
      .reduce(
        (chain, other) =>
          chain.then(() =>
            KanbanAPI.update(other.id, {
              ...other,
              item_details: {
                ...(other.item_details || {}),
                lead_primary: false,
              },
            })
          ),
        Promise.resolve()
      );
    emitter.emit('kanban.item.updated', updatedVisible);
    currentCardIdx.value = 0;
  } catch (e) {
    /* ignore */
  }
};

// Bug 3: + Outro funil — botao pra criar card em outro funil pro mesmo lead
const showAddFunnelDropdown = ref(false);
const creatingInFunnel = ref(false);

const availableFunnelsToAdd = computed(() => {
  const usedFunnelIds = new Set(
    sortedKanbanItems.value.map(i => Number(i.funnel_id))
  );
  return funnels.value.filter(
    f => f.active !== false && !usedFunnelIds.has(Number(f.id))
  );
});

// SiteUp V2 (2026-08-11): funil escolhido no "+", aguardando a etapa.
const pendingAddFunnel = ref(null);

// Etapas ordenadas por `position` — `Object.keys()` devolve a ordem de
// inserção do objeto, que não é a ordem que a pessoa vê no quadro.
const stagesOfFunnel = funnel =>
  Object.entries(funnel?.stages || {})
    .map(([key, st]) => ({
      key,
      name: st.name || key,
      color: st.color || '#64748B',
      position: Number(st.position ?? 0),
    }))
    .sort((a, b) => a.position - b.position);

const toggleAddFunnelDropdown = () => {
  showAddFunnelDropdown.value = !showAddFunnelDropdown.value;
  if (!showAddFunnelDropdown.value) pendingAddFunnel.value = null;
};

const onPickAddFunnel = funnel => {
  pendingAddFunnel.value = funnel;
};

const cancelAddFunnelPick = () => {
  pendingAddFunnel.value = null;
};

const createCardInFunnel = async (funnel, stageKey) => {
  if (!funnel || creatingInFunnel.value) return;
  try {
    creatingInFunnel.value = true;
    // Sem etapa escolhida (chamada antiga), cai na PRIMEIRA POR POSIÇÃO —
    // não na primeira chave do objeto, que era o comportamento anterior.
    const firstStageKey = stageKey || stagesOfFunnel(funnel)[0]?.key || 'lead';
    const payload = {
      funnel_id: funnel.id,
      funnel_stage: firstStageKey,
      position: 0,
      conversation_display_id: props.conversationId,
      item_details: {
        title: contactName.value || 'Novo card',
        description: '',
        conversation_id: props.conversationId,
      },
    };
    const { data } = await KanbanAPI.createItem(payload);
    showAddFunnelDropdown.value = false;
    pendingAddFunnel.value = null;
    if (typeof emitter !== 'undefined')
      emitter.emit('kanban.item.created', data);
    // Update currentChat.kanban_items in-place so sortedKanbanItems re-computes
    const chat = currentChat.value;
    if (chat?.id) {
      const existing = Array.isArray(chat.kanban_items)
        ? chat.kanban_items
        : [];
      const merged = [...existing.filter(i => i.id !== data.id), data];
      store.dispatch('updateConversationKanbanItems', {
        conversationId: chat.id,
        kanbanItems: merged,
      });
    }
    setTimeout(() => {
      const idx = sortedKanbanItems.value.findIndex(i => i.id === data.id);
      if (idx >= 0) currentCardIdx.value = idx;
    }, 100);
  } catch (e) {
    /* ignore */
  } finally {
    creatingInFunnel.value = false;
  }
};

const formatOfferValue = offer => {
  const currCode =
    (typeof offer.currency === 'string'
      ? offer.currency
      : offer.currency?.code) || 'BRL';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: currCode,
  }).format(Number(offer.value || 0));
};

const addOfferToItem = async offer => {
  if (!kanbanItemComputed.value) return;
  const current = kanbanItemComputed.value.item_details || {};
  const existingOffers = Array.isArray(current.offers) ? current.offers : [];
  const offerEntry = {
    id: offer.id,
    description: offer.title || offer.name || offer.description || '',
    name: offer.title || offer.name || offer.description || '',
    value: Number(offer.value || 0),
    currency: offer.currency || 'BRL',
    added_at: new Date().toISOString(),
  };
  const newOffers = [...existingOffers, offerEntry];
  const newValue = Number(current.value || 0) + Number(offer.value || 0);
  const updated = {
    ...kanbanItemComputed.value,
    item_details: { ...current, offers: newOffers, value: newValue },
  };
  try {
    await KanbanAPI.update(kanbanItemComputed.value.id, updated);
    emitter.emit('kanban.item.updated', updated);
    closeOffersDropdown();
    refreshChatKanbanItems(updated);
  } catch (e) {
    /* ignore */
  }
};

const removeOfferFromItem = async offerIdx => {
  if (!kanbanItemComputed.value) return;
  const current = kanbanItemComputed.value.item_details || {};
  const offers = Array.isArray(current.offers) ? [...current.offers] : [];
  const removed = offers.splice(offerIdx, 1)[0];
  if (!removed) return;
  const newValue = Math.max(
    0,
    Number(current.value || 0) - Number(removed.value || 0)
  );
  const updated = {
    ...kanbanItemComputed.value,
    item_details: { ...current, offers, value: newValue },
  };
  try {
    await KanbanAPI.update(kanbanItemComputed.value.id, updated);
    emitter.emit('kanban.item.updated', updated);
    refreshChatKanbanItems(updated);
  } catch (e) {
    /* ignore */
  }
};

const showKanbanForm = ref(false);
const previewData = ref(null);

// Adicione estas refs para controlar o modal de detalhes
const showDetailsModal = ref(false);
// Aba em que o card completo abre.
const detailsInitialTab = ref('contexto');
// Trava os seletores enquanto um move está em voo.
const kanbanBusy = ref(false);
const itemToShow = ref(null);

// Adicionar ref para agentsList
const agentsList = ref([]);

// Refs para checklist
const internalChecklists = ref([]);
const loadingChecklists = ref(false);

// Computed para trabalhar com múltiplos agentes
const agentInfo = computed(() => {
  if (
    kanbanItemComputed.value?.assigned_agents &&
    kanbanItemComputed.value.assigned_agents.length > 0
  ) {
    return kanbanItemComputed.value.assigned_agents.map(agent => ({
      id: Number(agent.id),
      name: agent.name,
      avatar_url: agent.avatar_url || '',
      assigned_at: agent.assigned_at,
      assigned_by: agent.assigned_by,
    }));
  }
  return [];
});

const isAgentAlreadyAssigned = agentId =>
  agentInfo.value.some(a => Number(a.id) === Number(agentId));

// Modal de atribuição rápida de agente
const showAgentAssignModal = ref(false);
const agentAssignLoading = ref(false);
const selectedAgentId = ref(null);
const agentSearch = ref('');

// Quando o array shrinka (ex: card deletado), corrige currentCardIdx via watcher fora do computed
watch(
  () => sortedKanbanItems.value.length,
  len => {
    if (len > 0 && currentCardIdx.value >= len) currentCardIdx.value = len - 1;
  }
);

// Formata a conversa atual para o select
const currentConversation = computed(() => {
  if (!currentChat.value) return null;

  return {
    id: currentChat.value.id,
    meta: currentChat.value.meta,
    messages: currentChat.value.messages || [],
  };
});

watch(
  () => props.conversationId,
  (newId, oldId) => {
    if (newId && newId !== oldId) {
      internalChecklists.value = [];
    }
  }
);

// Função para buscar checklists do item
const fetchChecklists = async () => {
  if (!kanbanItemComputed.value?.id) return;

  try {
    loadingChecklists.value = true;
    const response = await KanbanAPI.getChecklists(kanbanItemComputed.value.id);
    internalChecklists.value = Array.isArray(response.data.checklist)
      ? response.data.checklist
      : [];
  } catch (error) {
    return;
  } finally {
    loadingChecklists.value = false;
  }
};

watch(
  () => kanbanItemComputed.value?.id,
  async id => {
    if (id) await fetchChecklists();
    else internalChecklists.value = [];
  }
);

const currentFunnel = computed(() => {
  const item = previewData.value || kanbanItemComputed.value;
  if (!item?.funnel) return null;
  return item.funnel; // Usar o funil que vem diretamente do item
});

const kanbanStageLabel = computed(() => {
  const item = previewData.value || kanbanItemComputed.value;
  if (!item || !currentFunnel.value?.stages) return null;
  const stage = currentFunnel.value.stages[item.funnel_stage];
  return stage?.name || item.funnel_stage;
});

// Dados iniciais para o formulário do Kanban
const initialFormData = computed(() => {
  if (kanbanItemComputed.value) {
    // Se já existe um item, preservar todos os dados existentes
    return {
      ...kanbanItemComputed.value,
      description: kanbanItemComputed.value.item_details?.description || '',
      item_details: {
        ...kanbanItemComputed.value.item_details,
        description: kanbanItemComputed.value.item_details?.description || '',
        _agent: kanbanItemComputed.value.item_details?.agent_id
          ? agentsList.value.find(
              a => a.id === kanbanItemComputed.value.item_details.agent_id
            )
          : null,
      },
      _conversation: currentConversation.value,
    };
  }

  // Se é um novo item, usar os dados da conversa atual
  const assignedAgent = currentChat.value?.meta?.assignee;
  const priority = currentChat.value?.priority || 'none';

  return {
    title: contactName.value,
    description: '',
    funnel_id: funnels.value[0]?.id,
    funnel_stage: 'lead',
    item_details: {
      title: contactName.value,
      description: '',
      conversation_id: props.conversationId,
      agent_id: assignedAgent?.id || null,
      _agent: assignedAgent,
      priority: priority,
    },
    _conversation: currentConversation.value,
  };
});

const fetchFunnels = async () => {
  try {
    const response = await FunnelAPI.get();
    funnels.value = response.data;
  } catch (error) {
    /* ignore */
  }
};

const handleKanbanItemDeleted = payload => {
  const id = payload?.id;
  if (!id) return;
  // Limpa do cache local pra Outros Itens / sortedKanbanItems re-computar.
  itemsSameConversation.value = (itemsSameConversation.value || []).filter(
    i => i.id !== id
  );
  // Ajusta currentCardIdx se ele apontava pro item removido.
  if (currentCardIdx.value > 0) {
    const total = sortedKanbanItems.value.length;
    if (currentCardIdx.value >= total) {
      currentCardIdx.value = Math.max(0, total - 1);
    }
  }
};

// Adicionar função para carregar agentes
const fetchAgents = async () => {
  try {
    const { data } = await agents.get();
    agentsList.value = data;
  } catch (error) {
    /* ignore */
  }
};

onMounted(async () => {
  await Promise.all([fetchFunnels(), fetchAgents()]);
  emitter.on('kanban.item.deleted', handleKanbanItemDeleted);
});

onBeforeUnmount(() => {
  emitter.off('kanban.item.deleted', handleKanbanItemDeleted);
  // Garante limpeza dos listeners do dropdown de ofertas se desmontar aberto.
  window.removeEventListener('scroll', closeOffersDropdown, true);
  window.removeEventListener('resize', closeOffersDropdown);
});

const handleKanbanFormSave = async () => {
  try {
    showKanbanForm.value = false;
    previewData.value = null;

    // Emitir evento para atualizar o kanban
    emitter.emit('kanban.item.updated');

    // Emitir evento para atualizar a conversa se necessário
    if (currentChat.value) {
      emitter.emit('conversation.updated', currentChat.value.id);
    }
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao salvar alterações',
      type: 'error',
    });
  }
};

const hasScheduling = computed(() => {
  return !!(
    kanbanItemComputed.value?.item_details?.deadline_at ||
    kanbanItemComputed.value?.item_details?.scheduled_at
  );
});

const showScheduleForm = ref(false);
const schedDateTime = ref('');

const pad2 = n => String(n).padStart(2, '0');
const toLocalInputValue = d =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

const initScheduleForm = () => {
  const sa = kanbanItemComputed.value?.item_details?.scheduled_at;
  schedDateTime.value = '';
  if (sa) {
    try {
      schedDateTime.value = toLocalInputValue(new Date(sa));
    } catch {
      schedDateTime.value = '';
    }
  }
  showScheduleForm.value = true;
};

const scheduleFormValid = computed(() => !!schedDateTime.value);

const formattedSchedule = computed(() => {
  const sa = kanbanItemComputed.value?.item_details?.scheduled_at;
  if (!sa) return '';
  try {
    const d = new Date(sa);
    const pad = n => String(n).padStart(2, '0');
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} as ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
});

const cancelSchedule = async () => {
  if (!kanbanItemComputed.value?.id) return;
  try {
    const currentDetails = kanbanItemComputed.value.item_details || {};
    const updatedDetails = {
      ...currentDetails,
      scheduling_type: null,
      scheduled_at: null,
      deadline_at: null,
    };
    const { data } = await KanbanAPI.update(kanbanItemComputed.value.id, {
      item_details: updatedDetails,
    });
    if (data) {
      Object.assign(kanbanItemComputed.value, data);
      emitter.emit('kanban.item.updated', data);
      emitter.emit('newToastMessage', {
        message: 'Agendamento cancelado',
        type: 'success',
      });
    }
    showScheduleForm.value = false;
  } catch {
    emitter.emit('newToastMessage', {
      message: 'Erro ao cancelar',
      type: 'error',
    });
  }
};

const saveQuickSchedule = async () => {
  if (!scheduleFormValid.value || !kanbanItemComputed.value?.id) return;
  try {
    const currentDetails = kanbanItemComputed.value.item_details || {};
    const scheduledAt = new Date(schedDateTime.value).toISOString();
    const updatedDetails = {
      ...currentDetails,
      scheduling_type: 'schedule',
      scheduled_at: scheduledAt,
      deadline_at: null,
    };
    const { data } = await KanbanAPI.update(kanbanItemComputed.value.id, {
      item_details: updatedDetails,
    });
    if (data) {
      Object.assign(kanbanItemComputed.value, data);
      emitter.emit('kanban.item.updated', data);
      emitter.emit('newToastMessage', {
        message: 'Compromisso agendado!',
        type: 'success',
      });
    }
    showScheduleForm.value = false;
  } catch {
    emitter.emit('newToastMessage', {
      message: 'Erro ao agendar',
      type: 'error',
    });
  }
};

// Modifique a função handleShowDetails para usar o item completo
// SiteUp V2 (2026-08-11): navegação entre os cards do lead DENTRO do card
// aberto. A lista já existe aqui (`sortedKanbanItems`) — quem abre é quem tem
// os irmãos, então não há request novo.
const shownCardIdx = computed(() =>
  sortedKanbanItems.value.findIndex(
    card => String(card.id) === String(itemToShow.value?.id)
  )
);

const shownCardIsPrimary = computed(
  () =>
    !!sortedKanbanItems.value[shownCardIdx.value]?.item_details?.lead_primary
);

const handleShowDetails = item => {
  // Certifique-se de que o item tem todas as propriedades necessárias
  const formattedItem = {
    ...item,
    title: item.item_details?.title || contactName.value,
    description: item.item_details?.description || '',
    priority: item.item_details?.priority || 'none',
    funnel_stage: item.funnel_stage,
    item_details: {
      ...item.item_details,
      priority: item.item_details?.priority || 'none',
    },
  };

  itemToShow.value = formattedItem;
  // "Abrir card" abre o card COMPLETO. A tela satélite foi descontinuada em
  // 2026-08-11: o redesign passa a ser no próprio KanbanItemDetails, em vez de
  // manter duas telas para a mesma coisa.
  detailsInitialTab.value = 'contexto';
  showDetailsModal.value = true;
};

const goToCardAt = delta => {
  const alvo = sortedKanbanItems.value[shownCardIdx.value + delta];
  // Sem alvo (primeiro/último) o botão nem aparece; a guarda é para o caso de
  // a lista mudar embaixo com o modal aberto.
  if (alvo) handleShowDetails(alvo);
};

// Adicione esta função para atualizar os dados quando o modal for fechado
const handleDetailsUpdate = async () => {
  // NAO fechar o modal aqui - so atualizar a chat.kanban_items pra refletir
  // mudancas (offers, valor, primary, etc) feitas dentro do Detalhes.
  // O modal so deve fechar via @close ou click no botao X.
  if (!itemToShow.value?.id) return;
  try {
    const { data: fresh } = await KanbanAPI.getItem(itemToShow.value.id);
    if (fresh) {
      itemToShow.value = fresh;
      // Sync no store pra sidebar tambem refletir
      const chat = currentChat.value;
      if (chat?.id) {
        const existing = Array.isArray(chat.kanban_items)
          ? chat.kanban_items
          : [];
        const merged = existing.map(i => (i.id === fresh.id ? fresh : i));
        if (!merged.find(i => i.id === fresh.id)) merged.push(fresh);
        store.dispatch('updateConversationKanbanItems', {
          conversationId: chat.id,
          kanbanItems: merged,
        });
      }
    }
  } catch (error) {
    /* ignore */
  }
};

// SiteUp: refetch do item após mudar agentes, pra o painel (assigned_agents) e o
// store atualizarem na hora — antes só emitia evento local com o campo errado.
const refreshKanbanItemFromApi = async itemId => {
  try {
    const { data: fresh } = await KanbanAPI.getItem(itemId);
    if (fresh) {
      Object.assign(kanbanItemComputed.value, fresh);
      emitter.emit('kanban.item.updated', fresh);
      refreshChatKanbanItems(fresh);
    }
  } catch (e) {
    /* ignore */
  }
};

const assignAgent = async () => {
  if (
    !selectedAgentId.value ||
    agentAssignLoading.value ||
    !kanbanItemComputed.value?.id
  )
    return;
  agentAssignLoading.value = true;
  const itemId = kanbanItemComputed.value.id;
  try {
    await KanbanAPI.assignAgent(itemId, Number(selectedAgentId.value));
    await refreshKanbanItemFromApi(itemId);
    selectedAgentId.value = null;
    showAgentAssignModal.value = false;
    emitter.emit('newToastMessage', {
      message: 'Agente atribuído com sucesso',
      type: 'success',
    });
  } catch (error) {
    const serverMsg =
      error?.response?.data?.error ||
      error?.response?.data?.details?.join?.(', ') ||
      'Erro ao atribuir agente';
    emitter.emit('newToastMessage', {
      message: serverMsg,
      type: 'error',
    });
  } finally {
    agentAssignLoading.value = false;
  }
};

const removeAgent = async agentId => {
  if (!kanbanItemComputed.value?.id || !agentId) return;
  const itemId = kanbanItemComputed.value.id;
  try {
    await KanbanAPI.removeAgent(itemId, agentId);
    await refreshKanbanItemFromApi(itemId);
    emitter.emit('newToastMessage', {
      message: 'Agente removido com sucesso',
      type: 'success',
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao remover agente',
      type: 'error',
    });
  }
};

// Computed para lista filtrada de agentes
const filteredAgentList = computed(() => {
  if (!agentSearch.value) return agentsList.value;
  return agentsList.value.filter(agent =>
    (agent.name || '').toLowerCase().includes(agentSearch.value.toLowerCase())
  );
});

// Computed para o funil selecionado no modal
// Funções auxiliares para o header do modal de detalhes
const getItemStatusInfo = item => {
  const status = item?.item_details?.status?.toLowerCase();

  const statusMap = {
    won: {
      label: 'Ganho',
      class: 'bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-300',
    },
    lost: {
      label: 'Perdido',
      class: 'bg-n-ruby-9/10 text-n-ruby-11',
    },
  };

  // Se não há status definido, retorna "Aberto" com cor neutra
  if (!status) {
    return {
      label: 'Aberto',
      class: 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
    };
  }

  return (
    statusMap[status] || {
      label: 'Aberto',
      class: 'bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-300',
    }
  );
};

const getItemStatusClass = item => {
  return getItemStatusInfo(item).class;
};

const getItemStatusLabel = item => {
  return getItemStatusInfo(item).label;
};

const getPriorityInfo = item => {
  if (!item || !item.item_details?.priority) {
    return null;
  }
  const priority = item.item_details.priority.toLowerCase();
  if (priority === 'none') return null;

  const priorityMap = {
    high: {
      label: 'Alta',
      class: 'bg-n-ruby-3 dark:bg-n-ruby-8 text-n-ruby-9 dark:text-n-ruby-1',
    },
    medium: {
      label: 'Média',
      class:
        'bg-yellow-50 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-50',
    },
    low: {
      label: 'Baixa',
      class: 'bg-green-50 dark:bg-green-800 text-green-800 dark:text-green-50',
    },
  };

  return priorityMap[priority] || null;
};

const getConversationCustomAttributes = item => {
  const conversation = item?.conversation;
  if (!conversation?.custom_attributes) return [];

  // Filtrar apenas atributos não vazios
  const filtered = [];
  Object.entries(conversation.custom_attributes).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      filtered.push({ key, value });
    }
  });

  return filtered;
};

const getFormattedUpdatedAt = item => {
  if (!item?.updated_at) return '';
  try {
    const date = new Date(item.updated_at);
    return date.toLocaleString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    return '';
  }
};

const getFormattedDeadlineAt = item => {
  if (!item?.item_details?.deadline_at) return '';
  try {
    const date = new Date(item.item_details.deadline_at);
    return date.toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return '';
  }
};

const getStatusInfo = item => {
  if (!item?.item_details?.status) {
    return null;
  }
  const status = item.item_details.status;
  if (status === 'won') {
    const closedOffer = item.item_details.closed_offer;
    let label = 'Ganho';

    if (closedOffer) {
      const currency = closedOffer.currency || item.item_details.currency;
      const value = closedOffer.value;
      const formattedValue = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: currency?.code || 'BRL',
      }).format(value);

      label = `${label} - Oferta fechada: ${formattedValue}`;
    }

    return {
      label,
      class: 'bg-green-50 text-green-700 dark:bg-green-900 dark:text-green-300',
      icon: 'checkmark-circle',
    };
  }
  if (status === 'lost') {
    return {
      label: 'Perdido',
      class: 'bg-n-ruby-9/10 text-n-ruby-11',
      icon: 'dismiss-circle',
    };
  }
  return null;
};

// Itens do kanban com a mesma conversa vinculada

const fetchItemsSameConversation = async () => {
  const displayId =
    props.conversationId ??
    currentChat.value?.meta?.conversation_id ??
    currentChat.value?.id;
  if (!displayId) return;
  try {
    loadingSameConversation.value = true;
    const { data } = await KanbanAPI.getItemsByConversation(displayId);
    const list = data?.items ?? [];
    // Guarda lista completa - filtragem do item atual acontece em otherItemsSameConversation pra nao quebrar carousel
    itemsSameConversation.value = list;
  } catch (error) {
    itemsSameConversation.value = [];
  } finally {
    loadingSameConversation.value = false;
  }
};

// Watch para buscar itens da mesma conversa quando houver item
watch(
  () => props.conversationId,
  () => {
    if (props.conversationId) fetchItemsSameConversation();
    else itemsSameConversation.value = [];
  },
  { immediate: true }
);

// Funções para modal de mover funil
// SiteUp: troca de etapa em 1 CLIQUE direto na sidebar (setas + dropdown), sem
// abrir o modal "Mover". Usa moveToStage (1 request, devolve o item completo).
const stageMenuOpen = ref(false);
const orderedStages = computed(() => {
  const stages = currentFunnel.value?.stages || {};
  return Object.entries(stages)
    .map(([key, s]) => ({
      key,
      name: s.name || key,
      color: s.color || '#64748B',
      position: Number(s.position ?? 0),
    }))
    .sort((a, b) => a.position - b.position);
});
const currentStageIdx = computed(() =>
  orderedStages.value.findIndex(
    s => s.key === kanbanItemComputed.value?.funnel_stage
  )
);
const changeStage = async stageKey => {
  stageMenuOpen.value = false;
  const item = kanbanItemComputed.value;
  if (!item || !stageKey || stageKey === item.funnel_stage) return;
  kanbanBusy.value = true;
  try {
    const updated = await KanbanAPI.moveToStage(item.id, stageKey);
    if (updated) {
      emitter.emit('kanban.item.updated', updated);
      refreshChatKanbanItems(updated);
    }
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao mudar a etapa',
      type: 'error',
    });
  } finally {
    kanbanBusy.value = false;
  }
};

// Header do card (estilo mockup): título, iniciais do avatar e selo "criado pela IA".
const cardTitle = computed(
  () =>
    kanbanItemComputed.value?.item_details?.title ||
    contactName.value ||
    'Sem nome'
);
const isAiCreated = computed(() => {
  const notes = kanbanItemComputed.value?.item_details?.notes || [];
  return notes.some(n => /autom|pela ia/i.test((n?.text || '').toString()));
});

const priorityOptions = [
  { key: 'urgent', label: 'Urgente', color: '#7C3AED' },
  { key: 'high', label: 'Alta', color: '#EF4444' },
  { key: 'medium', label: 'Média', color: '#F59E0B' },
  { key: 'low', label: 'Baixa', color: '#10B981' },
  { key: 'none', label: 'Nenhuma', color: '#64748B' },
];
// SiteUp: edição rápida (modal enxuto) — título, valor, prioridade, descrição.
const showQuickEdit = ref(false);
const qeTitle = ref('');
const qeValue = ref('');
const qePriority = ref('none');
const qeDescription = ref('');
const qeSaving = ref(false);
const onQeValueInput = event => {
  qeValue.value = maskKanbanCurrencyTyping(event?.target?.value);
  if (event?.target) event.target.value = qeValue.value;
};
const saveQuickEdit = async () => {
  if (!kanbanItemComputed.value || qeSaving.value) return;
  qeSaving.value = true;
  try {
    const current = kanbanItemComputed.value.item_details || {};
    const num = parseKanbanCurrencyInput(qeValue.value);
    const details = {
      ...current,
      title: qeTitle.value.trim() || current.title,
      priority: qePriority.value,
      description: qeDescription.value,
    };
    if (num !== null && num >= 0) details.value = num;
    const updated = {
      ...kanbanItemComputed.value,
      item_details: details,
      skip_appointment_emit: true,
    };
    await KanbanAPI.update(kanbanItemComputed.value.id, updated);
    emitter.emit('kanban.item.updated', updated);
    refreshChatKanbanItems(updated);
    showQuickEdit.value = false;
    emitter.emit('newToastMessage', {
      message: 'Card atualizado',
      type: 'success',
    });
  } catch (e) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao salvar',
      type: 'error',
    });
  } finally {
    qeSaving.value = false;
  }
};
</script>

<template>
  <div class="p-4">
    <div v-if="kanbanItemComputed && currentFunnel" class="flex flex-col gap-4">
      <!-- Header do contato (estilo mockup): avatar + nome + selo "criado pela IA";
           setas de multi-card (se 2+) e "+ outro funil" ficam à direita. -->
      <div class="relative flex items-center gap-2.5">
        <button
          v-if="hasMultipleCards"
          type="button"
          class="rounded p-1 text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-11 dark:hover:bg-n-solid-2 dark:hover:text-n-slate-12"
          :title="t('KANBAN.PANEL_ACTIONS.PREVIOUS_CARD')"
          @click="prevCard"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div class="relative flex flex-1 flex-col min-w-0">
          <span
            class="line-clamp-2 break-words text-sm font-medium text-n-slate-12"
            :title="cardTitle"
          >
            {{ cardTitle }}
          </span>
          <!-- Funil e etapa em linhas próprias: no sidebar estreito o
               truncate de uma linha cortava "02 - Compra e Moradia · SYNC
               DIÁRIO" e o atendente não via onde o lead está. -->
          <span
            data-testid="kanban-card-subtitle"
            class="flex flex-col items-stretch gap-0.5 text-xs text-n-slate-10"
          >
            <span class="flex min-w-0 items-start gap-1">
              <svg
                v-if="isAiCreated"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="mt-0.5 shrink-0 text-cyan-500"
                :title="t('KANBAN.PANEL_ACTIONS.AI_CREATED')"
              >
                <path
                  d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z"
                />
              </svg>
              <span
                class="mt-1 size-2 shrink-0 rounded-full"
                :style="{
                  backgroundColor:
                    orderedStages[currentStageIdx]?.color || '#64748B',
                }"
              />
              <span
                data-testid="kanban-funnel-label"
                class="min-w-0 flex-1 break-words"
              >
                {{ currentCardFunnelName }}
              </span>
              <span v-if="hasMultipleCards" class="shrink-0 tabular-nums">
                {{ currentCardIdx + 1 }}/{{ totalCards }}
              </span>
            </span>
            <button
              type="button"
              data-testid="kanban-stage-pill"
              :disabled="kanbanBusy"
              :aria-expanded="stageMenuOpen"
              class="-my-0.5 inline-flex min-w-0 max-w-full cursor-pointer items-start gap-0.5 rounded py-0.5 text-left font-semibold text-n-blue-11 transition-colors hover:underline disabled:cursor-wait"
              @click="stageMenuOpen = !stageMenuOpen"
            >
              <span class="min-w-0 break-words">{{ kanbanStageLabel }}</span>
              <span class="i-ri-arrow-down-s-line mt-0.5 size-3.5 shrink-0" />
            </button>
          </span>
          <div
            v-if="stageMenuOpen"
            class="absolute left-0 top-full z-20 mt-1 max-h-64 w-56 overflow-auto rounded-md border border-n-weak bg-n-solid-1 shadow-lg"
          >
            <button
              v-for="s in orderedStages"
              :key="s.key"
              type="button"
              class="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-n-alpha-2"
              @click="changeStage(s.key)"
            >
              <span
                class="size-2 shrink-0 rounded-full"
                :style="{ backgroundColor: s.color }"
              />
              <span
                class="flex-1 truncate"
                :class="
                  s.key === kanbanItemComputed.funnel_stage
                    ? 'font-semibold text-n-slate-12'
                    : 'text-n-slate-11'
                "
              >
                {{ s.name }}
              </span>
              <span
                v-if="s.key === kanbanItemComputed.funnel_stage"
                class="i-ri-check-line size-3.5 shrink-0 text-n-blue-11"
              />
            </button>
          </div>
          <!-- Barrinha de fases (pedido do Lucas): um segmento por etapa,
               preenchidos até a atual, clicável para mover. -->
          <div
            v-if="orderedStages.length > 1"
            data-testid="kanban-panel-stage-track"
            class="mt-1.5 flex items-center gap-1"
          >
            <button
              v-for="(s, i) in orderedStages"
              :key="s.key"
              type="button"
              class="h-1 flex-1 rounded-full transition-colors disabled:cursor-wait"
              :class="
                i <= currentStageIdx
                  ? ''
                  : 'bg-n-alpha-2 hover:bg-n-alpha-black2'
              "
              :style="
                i <= currentStageIdx
                  ? {
                      backgroundColor:
                        orderedStages[currentStageIdx]?.color || '#7EB6FF',
                    }
                  : {}
              "
              :title="s.name"
              :aria-label="s.name"
              :disabled="kanbanBusy"
              @click="changeStage(s.key)"
            />
          </div>
        </div>
        <button
          v-if="hasMultipleCards"
          type="button"
          class="rounded p-1 text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-11 dark:hover:bg-n-solid-2 dark:hover:text-n-slate-12"
          :title="t('KANBAN.PANEL_ACTIONS.NEXT_CARD')"
          @click="nextCard"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <button
          v-if="hasMultipleCards && isCurrentCardPrimary"
          type="button"
          class="rounded p-1 text-amber-500"
          :title="t('KANBAN_ACTIONS.PRIMARY_CARD_DEFAULT')"
          disabled
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
            stroke="currentColor"
            stroke-width="1"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon
              points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            />
          </svg>
        </button>
        <button
          v-else-if="hasMultipleCards"
          type="button"
          class="rounded p-1 text-n-slate-10 hover:bg-amber-50 hover:text-amber-500 dark:hover:bg-amber-900/30"
          :title="t('KANBAN_ACTIONS.MAKE_PRIMARY')"
          @click="makePrimary"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polygon
              points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
            />
          </svg>
        </button>
        <!-- + Outro funil (sempre visivel se ha funis disponiveis) -->
        <div v-if="availableFunnelsToAdd.length > 0" class="relative">
          <button
            type="button"
            class="rounded p-1 text-cyan-600 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-900/30"
            :title="t('KANBAN_ACTIONS.ADD_LEAD_OTHER_FUNNEL')"
            :disabled="creatingInFunnel"
            @click="toggleAddFunnelDropdown"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          <!-- SiteUp V2 (2026-08-11): o "+" pergunta a ETAPA. Antes criava
               direto na primeira CHAVE do objeto de etapas — ordem de
               inserção, que nem respeita `position` — e por isso todo card
               nascia em "Qualificação" e alguém movia depois, sujando o
               histórico. A primeira etapa continua sendo o padrão sugerido:
               quem só quer criar continua a dois cliques. -->
          <div
            v-if="showAddFunnelDropdown"
            data-testid="kanban-add-funnel-menu"
            class="absolute right-0 top-full z-20 mt-1 max-h-64 w-60 overflow-auto rounded-md border border-n-weak bg-n-solid-1 shadow-lg dark:border-n-weak dark:bg-n-solid-1"
          >
            <template v-if="!pendingAddFunnel">
              <div
                class="border-b border-n-weak px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-n-slate-11"
              >
                {{ t('KANBAN_ACTIONS.ADD_TO_OTHER_FUNNEL') }}
              </div>
              <button
                v-for="funnel in availableFunnelsToAdd"
                :key="funnel.id"
                type="button"
                :data-testid="`kanban-add-funnel-${funnel.id}`"
                class="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs hover:bg-cyan-50 dark:hover:bg-n-solid-2"
                :disabled="creatingInFunnel"
                @click="onPickAddFunnel(funnel)"
              >
                <span class="min-w-0 truncate font-medium text-n-slate-12">{{
                  funnel.name
                }}</span>
                <span
                  class="i-ri-arrow-right-s-line size-3.5 shrink-0 text-n-slate-11"
                />
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                data-testid="kanban-add-funnel-back"
                class="flex w-full items-center gap-1 border-b border-n-weak px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-n-slate-11 hover:bg-n-alpha-2"
                @click="cancelAddFunnelPick"
              >
                <span class="i-ri-arrow-left-s-line size-3.5 shrink-0" />
                <span class="min-w-0 truncate">{{
                  pendingAddFunnel.name
                }}</span>
              </button>
              <p
                v-if="!stagesOfFunnel(pendingAddFunnel).length"
                class="m-0 px-2 py-2 text-xs text-n-slate-11"
              >
                {{ t('KANBAN.PANEL_ACTIONS.NO_STAGES_TO_CREATE') }}
              </p>
              <button
                v-for="(st, i) in stagesOfFunnel(pendingAddFunnel)"
                :key="st.key"
                type="button"
                :data-testid="`kanban-add-stage-${st.key}`"
                class="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs hover:bg-cyan-50 dark:hover:bg-n-solid-2"
                :disabled="creatingInFunnel"
                @click="createCardInFunnel(pendingAddFunnel, st.key)"
              >
                <span
                  class="size-2 shrink-0 rounded-full"
                  :style="{ backgroundColor: st.color }"
                />
                <span class="min-w-0 flex-1 truncate text-n-slate-12">{{
                  st.name
                }}</span>
                <span
                  v-if="i === 0"
                  class="shrink-0 text-[10px] text-n-slate-11"
                >
                  {{ t('KANBAN.PANEL_ACTIONS.DEFAULT_STAGE') }}
                </span>
              </button>
            </template>
          </div>
        </div>
      </div>
      <!-- Valor (Opção 1: número em destaque, editável ao clicar) -->
      <div>
        <p class="mb-0.5 text-xs font-medium text-n-slate-11">
          {{ t('KANBAN.CONVERSATION_ACTIONS.VALUE_LABEL') }}
          <span v-if="quickValueSaving" class="text-[10px] text-n-slate-10">
            {{ t('KANBAN_ACTIONS.SAVING_DOT') }}
          </span>
        </p>
        <input
          v-if="quickValueEditing"
          :value="quickValueDraft"
          type="text"
          inputmode="decimal"
          :placeholder="t('KANBAN_ACTIONS.CURRENCY_PLACEHOLDER')"
          class="w-full rounded-md border border-cyan-400 bg-n-solid-1 px-2 py-1 text-2xl font-extrabold tracking-tight text-n-slate-12 outline-none focus:ring-2 focus:ring-cyan-200 dark:border-cyan-500 dark:bg-n-solid-1 dark:text-n-slate-12"
          @input="onQuickValueInput"
          @keydown.enter.prevent="saveQuickValue"
          @keydown.esc.prevent="cancelQuickValueEdit"
          @blur="saveQuickValue"
        />
        <button
          v-else
          type="button"
          class="-mx-1 block max-w-full truncate rounded px-1 text-left text-2xl font-extrabold tracking-tight text-n-slate-12 hover:bg-n-solid-2 dark:text-n-slate-12 dark:hover:bg-n-solid-2"
          :title="t('KANBAN_ACTIONS.CLICK_TO_EDIT')"
          @click="startQuickValueEdit"
        >
          {{ quickValueDisplay }}
        </button>
      </div>

      <!-- Ofertas anexadas (detalhamento do valor) -->
      <div
        v-if="kanbanItemComputed.item_details?.offers?.length"
        class="-mt-1 flex flex-col gap-1"
      >
        <div
          v-for="(offer, idx) in kanbanItemComputed.item_details.offers"
          :key="idx"
          class="flex items-center justify-between rounded-md bg-cyan-50 px-2 py-1 text-xs dark:bg-cyan-900/20"
        >
          <span class="truncate text-n-slate-12">
            <span class="font-medium">{{
              offer.description || offer.name || offer.title
            }}</span>
            <span class="ml-1 text-n-slate-11">{{
              `+${formatOfferValue(offer)}`
            }}</span>
          </span>
          <button
            type="button"
            class="ml-2 text-n-slate-10 hover:text-n-ruby-11"
            :title="t('KANBAN_ACTIONS.REMOVE_OFFER')"
            @click="removeOfferFromItem(idx)"
          >
            {{ t('KANBAN_ACTIONS.REMOVE_OFFER_MARK') }}
          </button>
        </div>
      </div>

      <!-- SiteUp V2 (revisão do Lucas, 2026-08-11): o módulo é MÍNIMO.
           Etapa é o campo ali em cima (clica e escolhe direto), valor logo
           abaixo, e SÓ duas ações: Agendar e Oferta. Atribuir, prioridade,
           notas, checklist e os outros cards da conversa moram no CARD —
           aparecem depois de "Abrir card", nunca aqui. -->
      <div class="flex flex-col gap-3">
        <div
          class="flex flex-wrap items-center gap-2"
          data-testid="kanban-quick-actions"
        >
          <button
            type="button"
            data-testid="kanban-quick-schedule"
            class="inline-flex min-h-8 cursor-pointer items-center rounded-full border border-n-blue-11/60 px-3 text-[12.5px] font-bold text-n-blue-11 transition-colors hover:bg-n-blue-11/10"
            @click="
              showScheduleForm ? (showScheduleForm = false) : initScheduleForm()
            "
          >
            {{
              hasScheduling
                ? t('KANBAN.PANEL_ACTIONS.RESCHEDULE')
                : t('KANBAN.PANEL_ACTIONS.SCHEDULE')
            }}
          </button>
          <div class="relative">
            <button
              ref="offerBtn"
              type="button"
              data-testid="kanban-quick-offer"
              class="inline-flex min-h-8 cursor-pointer items-center rounded-full border border-n-blue-11/60 px-3 text-[12.5px] font-bold text-n-blue-11 transition-colors hover:bg-n-blue-11/10"
              @click="toggleOffersDropdown"
            >
              {{ t('KANBAN.PANEL_ACTIONS.OFFER') }}
            </button>
            <Teleport to="body">
              <div
                v-if="showOffersDropdown"
                :style="offersDropdownStyle"
                class="z-[60] overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 shadow-xl dark:border-n-weak dark:bg-n-solid-1"
              >
                <div
                  class="flex items-center gap-2 border-b border-n-weak p-2.5 dark:border-n-weak"
                >
                  <div class="relative flex-1">
                    <span
                      class="pointer-events-none absolute inset-y-0 left-2 flex items-center text-n-slate-10"
                    >
                      <svg
                        width="13"
                        height="13"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <circle cx="11" cy="11" r="8" />
                        <path d="m21 21-4.3-4.3" />
                      </svg>
                    </span>
                    <input
                      v-model="offersSearchTerm"
                      type="text"
                      :placeholder="t('KANBAN_ACTIONS.SEARCH_OFFER')"
                      class="w-full rounded-xl border border-n-weak bg-n-solid-1 py-1.5 pl-7 pr-2 text-xs text-n-slate-12 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 dark:border-n-weak dark:bg-n-solid-2 dark:text-n-slate-12"
                      @click.stop
                    />
                  </div>
                  <button
                    type="button"
                    class="shrink-0 rounded-xl p-1.5 text-n-slate-11 hover:bg-n-solid-3 hover:text-n-slate-11 dark:text-n-slate-10 dark:hover:bg-n-solid-2 dark:hover:text-n-slate-12"
                    :title="t('KANBAN.CONVERSATION_ACTIONS.CLOSE')"
                    @click.stop="closeOffersDropdown"
                  >
                    <span class="i-ri-close-line size-3.5" />
                  </button>
                </div>
                <div class="max-h-64 overflow-y-auto p-1">
                  <div
                    v-if="offersLoading"
                    class="p-3 text-center text-xs text-n-slate-11"
                  >
                    {{ t('KANBAN.CONVERSATION_ACTIONS.LOADING') }}
                  </div>
                  <div
                    v-else-if="!offersList.length"
                    class="p-3 text-center text-xs text-n-slate-11"
                  >
                    {{ t('KANBAN_ACTIONS.NO_OFFERS') }}
                  </div>
                  <div
                    v-else-if="!filteredOffersList.length"
                    class="p-3 text-center text-xs text-n-slate-11"
                  >
                    {{
                      t('KANBAN_ACTIONS.NO_OFFERS_MATCH', {
                        term: offersSearchTerm,
                      })
                    }}
                  </div>
                  <button
                    v-for="offer in filteredOffersList"
                    :key="offer.id"
                    type="button"
                    class="flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-cyan-50 dark:hover:bg-n-solid-2"
                    @click="addOfferToItem(offer)"
                  >
                    <span
                      class="inline-flex size-7 shrink-0 items-center justify-center rounded-xl bg-cyan-50 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-300"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path
                          d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"
                        />
                        <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                      </svg>
                    </span>
                    <span class="min-w-0 flex-1">
                      <span
                        class="block truncate text-xs font-medium text-n-slate-12"
                      >
                        {{ offer.title || offer.name || offer.description }}
                      </span>
                      <span class="block text-[10px] text-n-slate-11">{{
                        formatOfferValue(offer)
                      }}</span>
                    </span>
                  </button>
                </div>
              </div>
            </Teleport>
          </div>
        </div>

        <!-- Agendamento atual (quando já marcado) -->
        <div
          v-if="hasScheduling && !showScheduleForm"
          class="flex items-center justify-between rounded-md bg-n-solid-2 px-2.5 py-1.5 dark:bg-n-solid-2/50"
        >
          <span
            class="flex items-center gap-1.5 text-xs font-medium text-n-slate-12"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M8 2v4" />
              <path d="M16 2v4" />
              <rect width="18" height="18" x="3" y="4" rx="2" />
              <path d="M3 10h18" />
            </svg>
            {{ formattedSchedule }}
          </span>
          <button
            type="button"
            class="text-xs font-medium text-n-ruby-11 hover:underline"
            @click="cancelSchedule"
          >
            {{ t('KANBAN.CONVERSATION_ACTIONS.CANCEL') }}
          </button>
        </div>

        <!-- Form inline de agendamento -->
        <div
          v-if="showScheduleForm"
          class="flex flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-2 p-2.5 dark:border-n-weak dark:bg-n-solid-2/50"
        >
          <div class="flex flex-col gap-1.5">
            <label class="text-[10px] font-medium text-n-slate-11">{{
              t('KANBAN_ACTIONS.DATE_TIME')
            }}</label>
            <input
              v-model="schedDateTime"
              type="datetime-local"
              class="w-full rounded-md border border-n-weak bg-n-solid-1 px-2.5 py-2 text-xs text-n-slate-11 focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 dark:bg-n-solid-2 dark:text-n-slate-12"
            />
          </div>
          <div class="flex justify-end gap-1.5">
            <button
              type="button"
              class="rounded-md px-2.5 py-1 text-xs font-medium text-n-slate-11 hover:bg-n-solid-3 dark:text-n-slate-10 dark:hover:bg-n-alpha-2"
              @click="showScheduleForm = false"
            >
              {{ t('KANBAN_ACTIONS.BACK') }}
            </button>
            <button
              type="button"
              class="rounded-md bg-amber-500 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-50"
              :disabled="!scheduleFormValid"
              @click="saveQuickSchedule"
            >
              {{ t('KANBAN_ACTIONS.CONFIRM') }}
            </button>
          </div>
        </div>

        <!-- O caminho para todo o resto. -->
        <button
          type="button"
          data-testid="kanban-quick-card"
          class="inline-flex min-h-8 w-fit cursor-pointer items-center gap-0.5 rounded-full border border-n-strong px-3 text-[12.5px] font-bold text-n-slate-12 transition-colors hover:bg-n-alpha-2"
          @click="handleShowDetails(kanbanItemComputed)"
        >
          {{ t('KANBAN.PANEL_ACTIONS.OPEN_CARD') }}
          <span class="i-ri-arrow-right-s-line size-3.5" />
        </button>
      </div>
    </div>

    <!-- Modal -->
    <Modal
      v-model:show="showKanbanForm"
      size="full-width"
      :on-close="
        () => {
          showKanbanForm = false;
          previewData.value = null;
        }
      "
    >
      <div class="p-6">
        <h3 class="text-lg font-extrabold mb-4 flex items-center gap-2">
          <i class="icon-funnel text-n-slate-11" />
          {{ t('KANBAN.CONVERSATION_ACTIONS.CREATE_KANBAN_ITEM') }}
        </h3>
        <KanbanItemForm
          v-if="funnels[0]"
          :funnel-id="initialFormData.funnel_id || funnels[0].id"
          :stage="initialFormData.funnel_stage || 'lead'"
          :initial-data="initialFormData"
          :is-editing="false"
          :current-conversation="currentConversation"
          @saved="handleKanbanFormSave"
          @close="showKanbanForm = false"
          @update:preview="newData => (previewData.value = newData)"
        />
      </div>
    </Modal>

    <!-- Modal de Detalhes -->
    <Modal
      v-model:show="showDetailsModal"
      size="full-width"
      :on-close="
        () => {
          showDetailsModal = false;
          itemToShow = null;
          handleDetailsUpdate();
        }
      "
    >
      <div
        class="flex flex-col max-h-[90dvh] sm:max-h-[85vh] w-full bg-n-solid-1 overflow-hidden"
      >
        <!-- Header igual ao KanbanItemDetailView -->
        <div
          class="border-b border-n-weak px-4 py-3 flex items-center justify-between"
        >
          <div class="flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-chevron-left-icon lucide-chevron-left cursor-pointer text-n-slate-11"
              @click="
                () => {
                  showDetailsModal = false;
                  itemToShow = null;
                  handleDetailsUpdate();
                }
              "
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            <div class="flex items-center gap-3">
              <h2 class="text-base font-medium">
                {{ itemToShow?.item_details?.title || 'Detalhes do Item' }}
              </h2>
              <!-- ‹ 1/2 › — o lead pode ter um card por funil, e navegar entre
                   eles não deveria exigir fechar o card e voltar ao painel. -->
              <div
                v-if="hasMultipleCards && shownCardIdx >= 0"
                data-testid="kanban-modal-cardsnav"
                class="flex items-center gap-1 text-xs text-n-slate-11"
              >
                <button
                  v-if="shownCardIdx > 0"
                  type="button"
                  data-testid="kanban-modal-prev-card"
                  class="rounded p-1 hover:bg-n-alpha-2"
                  :title="t('KANBAN.PANEL_ACTIONS.PREVIOUS_CARD')"
                  @click="goToCardAt(-1)"
                >
                  <span class="i-ri-arrow-left-s-line size-3.5" />
                </button>
                <span class="tabular-nums">
                  {{ shownCardIdx + 1 }}/{{ totalCards }}
                </span>
                <button
                  v-if="shownCardIdx < totalCards - 1"
                  type="button"
                  data-testid="kanban-modal-next-card"
                  class="rounded p-1 hover:bg-n-alpha-2"
                  :title="t('KANBAN.PANEL_ACTIONS.NEXT_CARD')"
                  @click="goToCardAt(1)"
                >
                  <span class="i-ri-arrow-right-s-line size-3.5" />
                </button>
                <span
                  v-if="shownCardIsPrimary"
                  data-testid="kanban-modal-primary-star"
                  class="i-ri-star-fill size-3.5 text-amber-500"
                  :title="t('KANBAN.PANEL_ACTIONS.PRIMARY_CARD')"
                />
              </div>
              <!-- Status do Item -->
              <span
                v-if="itemToShow"
                class="px-2.5 py-1 text-xs font-medium rounded-full"
                :class="getItemStatusClass(itemToShow)"
              >
                {{ getItemStatusLabel(itemToShow) }}
              </span>
              <span
                v-if="itemToShow && getPriorityInfo(itemToShow)"
                class="px-2.5 py-1 text-xs font-medium rounded-full"
                :class="getPriorityInfo(itemToShow).class"
              >
                {{ getPriorityInfo(itemToShow).label }}
              </span>
              <!-- Custom Attributes da Conversa -->
              <div
                v-if="
                  itemToShow &&
                  getConversationCustomAttributes(itemToShow).length > 0
                "
                class="flex items-center gap-2"
              >
                <span
                  v-for="attr in getConversationCustomAttributes(itemToShow)"
                  :key="attr.key"
                  class="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded-full"
                  :title="`${attr.key}: ${attr.value}`"
                >
                  {{ `${attr.key}: ${attr.value}` }}
                </span>
              </div>
              <!-- Updated At -->
              <div
                v-if="itemToShow && getFormattedUpdatedAt(itemToShow)"
                class="text-[10px] text-n-slate-11 ml-2 hidden md:block"
              >
                {{ t('KANBAN.CONVERSATION_ACTIONS.UPDATED_AT') }}
                {{ getFormattedUpdatedAt(itemToShow) }}
              </div>
            </div>
          </div>

          <div v-if="itemToShow" class="flex items-center gap-3 mr-12">
            <!-- Data limite -->
            <div
              v-if="getFormattedDeadlineAt(itemToShow)"
              class="flex items-center gap-1.5 rounded-lg border border-amber-600 bg-amber-100 px-3 py-1.5 shadow-sm transition-all duration-200"
            >
              <span
                class="i-ri-calendar-schedule-line size-3.5 text-amber-600 drop-shadow-sm"
              />
              <span class="text-xs font-medium tracking-wide text-amber-600">
                {{ t('KANBAN.CONVERSATION_ACTIONS.DEADLINE_LABEL') }}
                {{ getFormattedDeadlineAt(itemToShow) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Status Header -->
        <div
          v-if="getStatusInfo(itemToShow)"
          class="px-4 py-1.5 flex items-center gap-2 text-xs font-medium"
          :class="getStatusInfo(itemToShow).class"
        >
          <fluent-icon :icon="getStatusInfo(itemToShow).icon" size="14" />
          <span>{{ getStatusInfo(itemToShow).label }}</span>
          <span
            v-if="itemToShow?.item_details?.reason"
            class="text-[10px] italic"
          >
            {{ `- ${itemToShow.item_details.reason}` }}
          </span>
        </div>

        <!-- Content -->
        <div class="flex-1 overflow-auto min-h-0">
          <KanbanItemDetails
            v-if="itemToShow"
            :item-id="itemToShow.id"
            :initial-kanban-tab="detailsInitialTab"
            :show-header="false"
            :show-edit-button="false"
            @close="
              () => {
                showDetailsModal = false;
                itemToShow = null;
                handleDetailsUpdate();
              }
            "
            @item-updated="handleDetailsUpdate"
          />
        </div>
      </div>
    </Modal>

    <!-- Modal de edição rápida (SiteUp) -->
    <Modal
      v-model:show="showQuickEdit"
      :on-close="
        () => {
          showQuickEdit = false;
        }
      "
      show-close-button
      size="medium"
    >
      <div class="p-6">
        <div class="mb-5 flex items-center gap-3">
          <div
            class="flex size-10 items-center justify-center rounded-xl bg-cyan-50 dark:bg-cyan-900/20"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-cyan-500"
            >
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
            </svg>
          </div>
          <h3 class="text-lg font-extrabold text-n-slate-12">
            {{ t('KANBAN_ACTIONS.QUICK_EDIT') }}
          </h3>
        </div>
        <div class="flex flex-col gap-4">
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
              t('KANBAN_ACTIONS.TITLE')
            }}</label>
            <input
              v-model="qeTitle"
              type="text"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-n-weak dark:bg-n-solid-2 dark:text-n-slate-12"
              :placeholder="t('KANBAN_ACTIONS.CARD_NAME_PLACEHOLDER')"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
              t('KANBAN_ACTIONS.VALUE_BRL')
            }}</label>
            <input
              :value="qeValue"
              type="text"
              inputmode="decimal"
              class="w-full rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-n-weak dark:bg-n-solid-2 dark:text-n-slate-12"
              :placeholder="t('KANBAN_ACTIONS.CURRENCY_PLACEHOLDER')"
              @input="onQeValueInput"
            />
          </div>
          <div>
            <label class="mb-1.5 block text-xs font-medium text-n-slate-11">{{
              t('KANBAN_ACTIONS.PRIORITY')
            }}</label>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="p in priorityOptions"
                :key="p.key"
                type="button"
                class="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors"
                :class="
                  qePriority === p.key
                    ? 'border-transparent text-white'
                    : 'border-n-weak text-n-slate-11 hover:bg-n-solid-2 dark:border-n-weak dark:text-n-slate-11 dark:hover:bg-n-solid-2'
                "
                :style="
                  qePriority === p.key ? { backgroundColor: p.color } : {}
                "
                @click="qePriority = p.key"
              >
                <span
                  class="size-2 rounded-full"
                  :style="{
                    backgroundColor: qePriority === p.key ? '#fff' : p.color,
                  }"
                />
                {{ p.label }}
              </button>
            </div>
          </div>
          <div>
            <label class="mb-1 block text-xs font-medium text-n-slate-11">{{
              t('KANBAN_ACTIONS.DESCRIPTION')
            }}</label>
            <textarea
              v-model="qeDescription"
              rows="3"
              class="w-full resize-none rounded-xl border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 dark:border-n-weak dark:bg-n-solid-2 dark:text-n-slate-12"
              placeholder="Observações do lead..."
            />
          </div>
        </div>
        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="rounded-xl px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-solid-3 dark:text-n-slate-11 dark:hover:bg-n-solid-2"
            @click="showQuickEdit = false"
          >
            {{ t('KANBAN.CONVERSATION_ACTIONS.CANCEL') }}
          </button>
          <button
            type="button"
            class="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:opacity-50"
            :disabled="qeSaving"
            @click="saveQuickEdit"
          >
            {{
              qeSaving ? t('KANBAN_ACTIONS.SAVING') : t('KANBAN_ACTIONS.SAVE')
            }}
          </button>
        </div>
      </div>
    </Modal>

    <!-- Modal de atribuição rápida de agente -->
    <Modal
      v-model:show="showAgentAssignModal"
      :on-close="() => (showAgentAssignModal = false)"
      show-close-button
      size="medium"
    >
      <div class="p-4 bg-n-solid-1 rounded-md border border-n-weak shadow-lg">
        <h3 class="text-lg font-extrabold mb-4">
          {{ t('KANBAN.CONVERSATION_ACTIONS.MANAGE_ASSIGNED_AGENTS') }}
        </h3>

        <!-- Seção de agentes já atribuídos -->
        <div v-if="agentInfo.length > 0" class="mb-4">
          <h4 class="text-sm font-medium text-n-slate-11 mb-2">
            {{
              `${t('KANBAN.CONVERSATION_ACTIONS.ASSIGNED_AGENTS')} (${
                agentInfo.length
              })`
            }}
          </h4>
          <div class="space-y-2">
            <div
              v-for="agent in agentInfo"
              :key="agent.id"
              class="flex items-center justify-between p-2 bg-n-solid-2 rounded-md"
            >
              <div class="flex items-center gap-2">
                <Avatar
                  :name="agent.name"
                  :src="agent.avatar_url || ''"
                  :size="24"
                />
                <div>
                  <div class="text-sm font-medium text-n-slate-12">
                    {{ agent.name }}
                  </div>
                  <div class="text-xs text-n-slate-11">
                    {{ t('KANBAN.CONVERSATION_ACTIONS.ASSIGNED_ON') }}
                    {{
                      new Date(agent.assigned_at).toLocaleDateString('pt-BR')
                    }}
                  </div>
                </div>
              </div>
              <button
                class="p-1 text-n-slate-10 hover:text-n-ruby-11 transition-colors"
                @click="removeAgent(agent.id)"
              >
                <span class="i-ri-delete-bin-line size-4" />
              </button>
            </div>
          </div>
        </div>

        <!-- Seção para adicionar novos agentes -->
        <div class="border-t border-n-weak pt-4">
          <h4 class="text-sm font-medium text-n-slate-11 mb-2">
            {{ t('KANBAN.CONVERSATION_ACTIONS.ADD_NEW_AGENT') }}
          </h4>
          <input
            v-model="agentSearch"
            type="text"
            class="w-full mb-3 px-3 py-2 border border-n-weak rounded-md text-xs bg-n-solid-2 focus:ring-2 focus:ring-woot-500/20 focus:border-woot-500"
            :placeholder="t('KANBAN.CONVERSATION_ACTIONS.SEARCH_AGENTS')"
          />
          <div class="space-y-2 mb-4 max-h-40 overflow-y-auto">
            <button
              v-for="agent in filteredAgentList"
              :key="agent.id"
              type="button"
              class="flex items-center w-full px-3 py-1.5 rounded-md transition-colors gap-2 border border-n-weak text-xs font-normal hover:bg-n-alpha-2"
              :class="[
                Number(selectedAgentId) === Number(agent.id)
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/30 font-bold text-woot-600'
                  : '',
                isAgentAlreadyAssigned(agent.id)
                  ? 'opacity-50 cursor-not-allowed'
                  : '',
              ]"
              :disabled="isAgentAlreadyAssigned(agent.id)"
              @click="selectedAgentId = agent.id"
            >
              <Avatar
                :name="agent.name || 'Agente'"
                :src="agent.avatar_url || ''"
                :size="24"
                class="mr-2"
              />
              <span class="truncate">{{ agent.name || 'Agente' }}</span>
              <span
                v-if="isAgentAlreadyAssigned(agent.id)"
                class="ml-auto text-n-slate-10 text-xs"
              >
                {{ t('KANBAN.CONVERSATION_ACTIONS.ALREADY_ASSIGNED') }}
              </span>
              <span
                v-else-if="Number(selectedAgentId) === Number(agent.id)"
                class="ml-auto text-woot-500 text-xs font-semibold"
              >
                {{ t('KANBAN.CONVERSATION_ACTIONS.SELECTED') }}
              </span>
            </button>
            <div
              v-if="filteredAgentList.length === 0"
              class="px-3 py-2 text-n-slate-10 text-xs"
            >
              {{ t('KANBAN.CONVERSATION_ACTIONS.NO_AGENT_FOUND') }}
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            @click="showAgentAssignModal = false"
          >
            {{ t('KANBAN.CONVERSATION_ACTIONS.CLOSE') }}
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            :is-loading="agentAssignLoading"
            :disabled="
              !selectedAgentId ||
              agentAssignLoading ||
              isAgentAlreadyAssigned(selectedAgentId)
            "
            @click="assignAgent"
          >
            {{ t('KANBAN.CONVERSATION_ACTIONS.ADD_AGENT') }}
          </Button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
/* Estilos específicos para o conteúdo markdown das notas */
.note-content :deep(ul),
.note-content :deep(ol) {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.note-content :deep(li) {
  margin: 0.25rem 0;
  padding-left: 0.25rem;
}

.note-content :deep(ul ul),
.note-content :deep(ol ol),
.note-content :deep(ul ol),
.note-content :deep(ol ul) {
  margin: 0.25rem 0;
  padding-left: 1rem;
}

/* Garante que as bolinhas das listas não vazem */
.note-content :deep(.note-text) {
  contain: layout style paint;
}

/* Estilos para markdown renderizado */
.prose {
  font-size: 14px !important;
  @apply leading-relaxed;

  :deep(p) {
    @apply mb-2 last:mb-0;
    font-size: 14px !important;
  }

  :deep(ul) {
    @apply list-disc list-inside mb-2 space-y-1;
  }

  :deep(ol) {
    @apply list-decimal list-inside mb-2 space-y-1;
  }

  :deep(li) {
    @apply leading-relaxed;
    font-size: 14px !important;
  }

  :deep(strong) {
    @apply font-semibold;
    font-size: 14px !important;
  }

  :deep(em) {
    @apply italic;
    font-size: 14px !important;
  }

  :deep(code) {
    @apply bg-n-solid-3 px-1 py-0.5 rounded font-mono;
    font-size: 13px !important;
  }

  :deep(pre) {
    @apply bg-n-solid-3 p-2 rounded font-mono overflow-x-auto mb-2;
    font-size: 13px !important;
  }

  :deep(a) {
    @apply text-woot-500 hover:text-woot-600 underline;
    font-size: 14px !important;
  }
}
</style>
